// 대나무 숲, 눈 더미·소나무·겨울나무, 내리는 눈, 하늘, 먼 후지산. 프로토타입 583–619, 661–753.
import * as THREE from 'three';
import { S, mk } from '../../engine/geometry';
import { plush } from '../../engine/materials';
import { angDiff, rnd } from '../../engine/math';
import { steamTex } from '../../engine/textures';
import { inPool, inRect } from './layout';
import { snowMat } from './materials';
import type { QuietScene } from './scene';

const MIST = 0xDCE5EC;
const culmMats = ['#8DA858', '#7E9A4C', '#9AAE62'].map((c) => plush(c, '#D4E2B4', 0.05, true));
const nodeMat = plush('#6E8840', '#B8CC92', 0.03, true), bLeafMats = ['#4E7A3A', '#5C8A44'].map((c) => plush(c, '#9CC08A', 0.04, true));
const leafGeo = new THREE.SphereGeometry(1, 6, 4);
const leafA = plush('#46704A', '#8DB08A', 0.05, true), leafB = plush('#527C52', '#9ABC94', 0.05, true), bark = plush('#6E5038', '#9C7A5E', 0.04, true);
const birch = plush('#D8D4CC', '#FFFFFF', 0.05, true), birchDark = plush('#7E7870', '#A8A29A', 0.03, true);

/** 통 탕 둘레와 대나무 길가의 대나무 숲. */
export function buildBamboo(Q: QuietScene): void {
  const { scenery, L } = Q, BP = L.pools[7]!;
  function bamboo(x: number, z: number, h: number, sB: number) {
    const r0 = 0.055 + rnd(sB) * 0.03, lean = (rnd(sB + 1) - 0.5) * 0.08, ld = rnd(sB + 2) * Math.PI * 2;
    const g = new THREE.Group(); g.position.set(x, 0, z); g.rotation.set(Math.sin(ld) * lean, 0, Math.cos(ld) * lean); scenery.add(g);
    const culm = mk(new THREE.CylinderGeometry(r0 * 0.75, r0, h, 8), culmMats[sB % 3]!); culm.position.y = h / 2; g.add(culm);
    const sg = 0.5 + rnd(sB + 3) * 0.2;
    for (let y = sg; y < h - 0.2; y += sg) {
      const nd = mk(new THREE.CylinderGeometry(r0 * 1.13, r0 * 1.13, 0.035, 8), nodeMat); nd.position.y = y; nd.castShadow = false; g.add(nd);
      if (y > h * 0.55) {
        const br = new THREE.Group(); br.position.y = y; br.rotation.y = rnd(sB + y * 13) * Math.PI * 2; g.add(br);
        const bl = 0.45 + rnd(sB + y * 17) * 0.5, tipX = Math.sin(1.05) * bl, tipY = Math.cos(1.05) * bl;
        const twig = mk(new THREE.CylinderGeometry(0.008, 0.012, bl, 4), nodeMat); twig.castShadow = false; twig.rotation.z = -1.05; twig.position.set(tipX / 2, tipY / 2, 0); br.add(twig);
        for (let k = 0; k < 4; k++) {
          const lf = mk(leafGeo, bLeafMats[(sB + k) % 2]!); lf.castShadow = k === 0; lf.scale.set(0.035, 0.008, 0.26);
          lf.position.set(tipX - 0.05 * k, tipY - 0.02 * k, (k - 1.5) * 0.06); lf.rotation.set(0.35 + 0.15 * k, (k - 1.5) * 0.7 + Math.PI / 2, -0.5); br.add(lf);
        }
        if (rnd(sB + y * 7) < 0.35) { const sn = mk(S(0.07, 8, 6), snowMat); sn.scale.set(1.4, 0.5, 1); sn.position.set(tipX, tipY + 0.03, 0); br.add(sn); }
      }
    }
    L.obstacles.push({ x, z, r: 0.3 });
  }
  let bSeed = 9000;
  for (let i = 0; i < 70; i++) {
    const a = rnd(i + 4300) * Math.PI * 2;
    if (Math.abs(angDiff(a, BP.gaps[0]!)) < 0.8) continue;
    const rr = BP.r + 2.6 + rnd(i + 4400) * 3.4;
    bamboo(BP.x + Math.cos(a) * rr, BP.z + Math.sin(a) * rr, 6 + rnd(i + 4500) * 3, bSeed += 7);
  }
  const path = Q.paths[1]!;
  path.getSpacedPoints(40).forEach((p, i) => {
    if (i < 24) return;
    const q = path.getPoint(Math.min(1, i / 40 + 0.02)), tx = q.x - p.x, tz = q.z - p.z, l = Math.hypot(tx, tz) || 1;
    [-1, 1].forEach((side) => {
      if (rnd(i * 3 + side + 4700) < 0.3) return;
      const off = 1.8 + rnd(i * 5 + side + 4800) * 1.4;
      bamboo(p.x - tz / l * off * side, p.z + tx / l * off * side, 5.5 + rnd(i + side + 4900) * 3, bSeed += 7);
    });
  });
}

/** 눈 더미, 소나무와 자작나무 같은 겨울나무를 광장·길·개울·장애물을 피해 흩어 심는다. */
export function buildTrees(Q: QuietScene): void {
  const { scenery, L } = Q, BP = L.pools[7]!;
  function pine(x: number, z: number, sc: number) {
    const g = new THREE.Group(); g.position.set(x, 0, z); g.scale.setScalar(sc);
    const tr = mk(new THREE.CylinderGeometry(0.14, 0.2, 1.0, 8), bark); tr.position.y = 0.5; g.add(tr);
    ([[1.25, 1.5, 1.2], [1.0, 1.3, 2.0], [0.7, 1.1, 2.7]] as const).forEach(([r, h, y], i) => {
      const c = mk(new THREE.ConeGeometry(r, h, 12), i % 2 ? leafB : leafA); c.position.y = y; g.add(c);
      const sn = mk(new THREE.ConeGeometry(r * 0.66, h * 0.58, 12), snowMat); sn.position.y = y + h * 0.24; g.add(sn);
    });
    scenery.add(g);
  }
  function bareTree(x: number, z: number, sc: number, sd: number) {
    const g = new THREE.Group(); g.position.set(x, 0, z); g.scale.setScalar(sc); scenery.add(g);
    const H = 5 + rnd(sd) * 2;
    const trunk = mk(new THREE.CylinderGeometry(0.09, 0.17, H, 8), birch); trunk.position.y = H / 2; g.add(trunk);
    for (let y = 0.6; y < H * 0.7; y += 0.55) { const mark = mk(new THREE.CylinderGeometry(0.15 - y * 0.012, 0.15 - y * 0.012, 0.04, 8), birchDark); mark.position.y = y + rnd(sd + y) * 0.2; mark.scale.set(1.02, 1, 1.02); mark.castShadow = false; g.add(mark); }
    const nB = 6 + Math.floor(rnd(sd + 1) * 3);
    for (let i = 0; i < nB; i++) {
      const y = H * (0.45 + 0.5 * i / nB), a = rnd(sd + i * 3) * Math.PI * 2, len = (1.6 - 0.8 * i / nB) * (0.8 + rnd(sd + i * 5) * 0.4), tilt = 0.55 + rnd(sd + i * 7) * 0.35;
      const br = new THREE.Group(); br.position.y = y; br.rotation.y = a; g.add(br);
      const b = mk(new THREE.CylinderGeometry(0.02, 0.055, len, 5), birchDark); b.rotation.z = -tilt; b.position.set(Math.sin(tilt) * len / 2, Math.cos(tilt) * len / 2, 0); br.add(b);
      const ex = Math.sin(tilt) * len, ey = Math.cos(tilt) * len;
      for (let k = 0; k < 2; k++) {
        const tl = len * 0.45, tt = tilt - 0.5 + k * 0.9;
        const tw = mk(new THREE.CylinderGeometry(0.008, 0.02, tl, 4), birchDark); tw.castShadow = false;
        tw.rotation.set((k ? 0.5 : -0.5), 0, -tt); tw.position.set(ex * 0.7 + Math.sin(tt) * tl / 2, ey * 0.7 + Math.cos(tt) * tl / 2, (k ? 0.08 : -0.08)); br.add(tw);
      }
      if (rnd(sd + i * 11) < 0.6) { const sn = mk(S(0.09, 8, 5), snowMat); sn.scale.set(len * 2.2, 0.45, 1.2); sn.position.set(Math.sin(tilt) * len / 2, Math.cos(tilt) * len / 2 + 0.05, 0); sn.rotation.z = -tilt + Math.PI / 2; br.add(sn); }
    }
  }
  function mound(x: number, z: number, sc: number) { const m = mk(S(0.7, 20, 12), snowMat); m.scale.set(1.5 * sc, 0.5 * sc, 1.15 * sc); m.position.set(x, 0, z); scenery.add(m); L.obstacles.push({ x, z, r: 0.95 * sc }); }
  ([[-11, 9.5, 1.3], [11.5, 10, 1.2], [-8.8, -15.5, 1.1], [8.8, -15.5, 1.1], [-4.4, 12.6, 1.2], [4.6, 12.8, 1.0], [-17, -9, 1.0], [17, -9, 1.0]] as const).forEach(([x, z, sc]) => mound(x, z, sc));

  const trees: { x: number; z: number; r: number }[] = [];
  for (let i = 0; i < 1600 && trees.length < 95; i++) {
    const x = (rnd(i + 5000) - 0.5) * 76, z = (rnd(i + 6000) - 0.5) * 66 - 9;
    const dPlaza = Math.hypot(x, (z + 2) * 1.15);
    if (z > 15 || dPlaza < 17 || Math.hypot(x, z + 6) > 37) continue;
    if (z > 1 && Math.abs(x) < 26) continue;
    if (L.pools.some((P) => inPool(P, x, z, P.name === 'hidden' ? (P === BP ? 7.0 : 4.5) : 3.0))) continue;
    if (Q.pathPts.some((p) => Math.hypot(x - p.x, z - p.z) < 2.3)) continue;
    if (Q.creekPts.some((p) => Math.hypot(x - p.x, z - p.z) < 2.0)) continue;
    if (trees.some((t) => Math.hypot(x - t.x, z - t.z) < t.r + 1.3)) continue;
    if (L.obstacles.some((o) => o.rect ? inRect(o, x, z, 2) : Math.hypot(x - o.x, z - o.z) < o.r + 1.6)) continue;
    const sc = 0.9 + rnd(i + 7000) * 0.5, bare = rnd(i + 8000) < 0.38;
    trees.push({ x, z, r: 1.0 * sc }); L.obstacles.push({ x, z, r: bare ? 0.4 : 0.75 * sc });
    if (bare) bareTree(x, z, sc, i + 9500); else pine(x, z, sc);
  }
  ([[-15.5, -3.5], [15.8, -2.8], [-20.5, 5], [20.5, 8]] as const).forEach(([x, z], i) => { bareTree(x, z, 1.05, 777 + i * 31); L.obstacles.push({ x, z, r: 0.4 }); });
}

export interface Snow { points: THREE.Points; pos: Float32Array; spd: Float32Array }

/** 내리는 눈 700송이 (움직임 줄이기 설정이면 없음). 카메라 대상을 따라다닌다. */
export function buildSnow(scene: THREE.Object3D): Snow {
  const reduceMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  const n = reduceMotion ? 0 : 700, pos = new Float32Array(n * 3), spd = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    pos[i * 3] = (rnd(i + 900) - 0.5) * 34; pos[i * 3 + 1] = rnd(i + 1900) * 13; pos[i * 3 + 2] = (rnd(i + 2900) - 0.5) * 30 - 2;
    spd[i] = 0.25 + rnd(i + 3900) * 0.25;
  }
  const geo = new THREE.BufferGeometry(); geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  const points = new THREE.Points(geo, new THREE.PointsMaterial({ color: 0xFFFFFF, size: 0.12, map: steamTex(), transparent: true, opacity: 0.9, depthWrite: false }));
  scene.add(points);
  return { points, pos, spd };
}

export function tickSnow(s: Snow, t: number, dt: number, x: number, z: number): void {
  const n = s.spd.length;
  if (!n) return;
  for (let i = 0; i < n; i++) {
    s.pos[i * 3 + 1]! -= s.spd[i]! * dt;
    s.pos[i * 3]! += Math.sin(t * 0.4 + i) * 0.08 * dt;
    if (s.pos[i * 3 + 1]! < 0) s.pos[i * 3 + 1] = 13;
  }
  s.points.geometry.attributes.position!.needsUpdate = true;
  s.points.position.set(x, 0, z);
}

/** 위는 옅은 하늘색, 지평선은 안개색으로 번지는 하늘 구. */
export function buildSky(scene: THREE.Object3D): { sky: THREE.Mesh; fuji: THREE.Mesh } {
  const g = new THREE.SphereGeometry(460, 32, 16), p = g.attributes.position!, col = new Float32Array(p.count * 3);
  const top = new THREE.Color('#BCD2E6'), hor = new THREE.Color(MIST), c = new THREE.Color();
  for (let i = 0; i < p.count; i++) { const h = Math.max(0, p.getY(i) / 460); c.copy(hor).lerp(top, Math.pow(h, 0.6)); col[i * 3] = c.r; col[i * 3 + 1] = c.g; col[i * 3 + 2] = c.b; }
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  const sky = new THREE.Mesh(g, new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.BackSide, fog: false, depthWrite: false }));
  sky.renderOrder = -1; scene.add(sky);

  // 꼭대기 3분의 1이 눈에 덮인 먼 후지산, 아래로 갈수록 안개에 묻힌다
  const H = 30, RB = 95, RT = 7, prof = [new THREE.Vector2(0, H - 1.4), new THREE.Vector2(RT * 0.6, H - 0.6)];
  for (let i = 0; i <= 28; i++) { const u = i / 28; prof.push(new THREE.Vector2(RT + (RB - RT) * u, H * (1 - Math.pow(u, 0.6)))); }
  const fg = new THREE.LatheGeometry(prof, 140); fg.computeVertexNormals();
  const pos = fg.attributes.position!, nor = fg.attributes.normal!, fcol = new Float32Array(pos.count * 3);
  const rock = new THREE.Color('#7E93A8'), snow = new THREE.Color('#F4F7FB'), haze = new THREE.Color(MIST), Ld = new THREE.Vector3(-0.5, 0.65, 0.6).normalize(), n = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i), h = y / H, ang = Math.atan2(z, x);
    n.set(nor.getX(i), nor.getY(i), nor.getZ(i)); if (n.x * x + n.z * z < 0) n.negate();
    const line = 0.62 - 0.13 * Math.pow(Math.max(0, Math.sin(ang * 11)), 4) - 0.06 * Math.pow(Math.max(0, Math.sin(ang * 27 + 1.3)), 3);
    c.copy(rock).lerp(snow, Math.min(1, Math.max(0, (h - line) / 0.025)));
    c.multiplyScalar(0.8 + 0.22 * Math.max(0, n.dot(Ld)));
    c.lerp(haze, Math.min(1, Math.max(0, 0.6 - h) * 1.25));
    fcol[i * 3] = c.r; fcol[i * 3 + 1] = c.g; fcol[i * 3 + 2] = c.b;
  }
  fg.setAttribute('color', new THREE.BufferAttribute(fcol, 3));
  const fuji = new THREE.Mesh(fg, new THREE.MeshBasicMaterial({ vertexColors: true, fog: false, side: THREE.DoubleSide }));
  fuji.position.set(-8, -3, -150); scene.add(fuji);
  return { sky, fuji };
}
